/**
 * Offline outbox + sync engine.
 *
 * - Writes that occur while offline are queued in IndexedDB (Dexie).
 * - When the browser comes back online, queued requests are replayed FIFO.
 * - The UI subscribes to outboxCount() to display the pending badge.
 *
 * Conflict policy: last-write-wins. Server is the source of truth; on
 * 409/422 the failed item is moved to the "failed" table for manual
 * review rather than silently retried forever.
 */
import Dexie, { type Table } from 'dexie';
import { useLiveQuery } from 'dexie-react-hooks';
import { useAuthStore } from '@/auth/store';

declare const __API_BASE__: string;

export interface OutboxItem {
  id?: number;
  clientId: string;             // UUID generated client-side for optimistic UI
  url: string;
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  createdAt: number;
  attempts: number;
  lastError?: string;
}

export interface FailedItem extends Omit<OutboxItem, 'id'> {
  id?: number;
  failedAt: number;
  status: number;
}

class PosDb extends Dexie {
  outbox!: Table<OutboxItem, number>;
  failed!: Table<FailedItem, number>;

  constructor() {
    super('pos-offline');
    this.version(1).stores({
      outbox: '++id, clientId, createdAt',
      failed: '++id, failedAt',
    });
  }
}

export const db = new PosDb();

function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export async function enqueue(req: Omit<OutboxItem, 'clientId' | 'createdAt' | 'attempts' | 'id'>): Promise<string> {
  const clientId = uuid();
  await db.outbox.add({
    clientId,
    url: req.url,
    method: req.method as OutboxItem['method'],
    body: req.body,
    createdAt: Date.now(),
    attempts: 0,
  });
  return clientId;
}

export function useOutboxCount(): number {
  return useLiveQuery(() => db.outbox.count(), [], 0) ?? 0;
}

export function useIsOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine;
}

let running = false;

export async function drainOutbox(): Promise<void> {
  if (running) return;
  running = true;
  try {
    const items = await db.outbox.orderBy('createdAt').toArray();
    for (const item of items) {
      const ok = await tryReplay(item);
      if (ok) {
        await db.outbox.delete(item.id!);
      } else {
        // Stop on first failure - preserve order
        break;
      }
    }
  } finally {
    running = false;
  }
}

async function tryReplay(item: OutboxItem): Promise<boolean> {
  const auth = useAuthStore.getState();
  const headers: Record<string, string> = {};
  if (item.body !== undefined) headers['Content-Type'] = 'application/json';
  if (auth.access) headers['Authorization'] = `Bearer ${auth.access}`;

  try {
    const res = await fetch(item.url, {
      method: item.method,
      credentials: 'include',
      headers,
      body: item.body === undefined ? undefined : JSON.stringify(item.body),
    });

    if (res.status >= 200 && res.status < 300) {
      return true;
    }
    if (res.status >= 400 && res.status < 500) {
      const text = await res.text().catch(() => '');
      await db.failed.add({
        clientId: item.clientId,
        url: item.url,
        method: item.method,
        body: item.body,
        createdAt: item.createdAt,
        attempts: item.attempts + 1,
        failedAt: Date.now(),
        status: res.status,
        lastError: text.slice(0, 500),
      });
      return true; // drop from outbox; visible in "failed" for user review
    }
    // 5xx: leave queued, will retry next online tick
    await db.outbox.update(item.id!, {
      attempts: item.attempts + 1,
      lastError: `HTTP ${res.status}`,
    });
    return false;
  } catch (err) {
    await db.outbox.update(item.id!, {
      attempts: item.attempts + 1,
      lastError: err instanceof Error ? err.message : 'network error',
    });
    return false;
  }
}

export function startSyncEngine(): void {
  if (typeof window === 'undefined') return;
  const tick = () => { void drainOutbox(); };
  window.addEventListener('online', tick);
  // Also poll while online - covers cases where the browser thinks it is
  // online but a single request actually timed out.
  setInterval(tick, 30_000);
  // First drain on boot in case there are leftovers from a previous session
  setTimeout(tick, 1000);
  // Suppress unused warning
  void __API_BASE__;
}
